import { CaretDownFilled, CaretRightFilled } from '@ant-design/icons';
import { Button, Card, Modal, notification, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import cn from 'classnames';
import { FC, MouseEvent } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { BASE_URI } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildAdminBaseUri } from '@/shared/lib';
import { DisabledActionTooltip } from '@/shared/ui';

import { billingModel } from '@/entities/billing';
import { BillingProductBlockReason } from '@/entities/organization';

import {
  MarkAsPaidModal,
  useMarkAsPaidModal,
} from '@/features/markAsPaidModal';

import { i18n } from '../../config';
import { BillingHistoryOrderData, useBillingHistory } from '../../hooks';
import { BillingHistoryCell } from '../billingHistoryCell/BillingHistoryCell';

import styles from './BillingHistory.module.scss';

const { Title } = Typography;

const selectOrdersLoadingState =
  billingModel.selectors.getLoadingStateSelector('orders');

const adminBaseUri = buildAdminBaseUri(BASE_URI);

type BillingHistoryDetailItem = {
  key: string;
  label: string;
  value?: string;
  isTechnicalValue?: boolean;
  href?: string;
};

type BillingHistoryDetailsSectionProps = {
  title: string;
  details: BillingHistoryDetailItem[];
  className?: string;
};

type BillingHistoryExpandIconProps = {
  expanded: boolean;
  onExpand: (
    record: BillingHistoryOrderData,
    event: MouseEvent<HTMLElement>,
  ) => void;
  record: BillingHistoryOrderData;
  expandLabel: string;
  collapseLabel: string;
  className?: string;
};

type BillingHistoryExpandIconLabels = {
  expandLabel: string;
  collapseLabel: string;
};

const BillingHistoryDetailsSection: FC<BillingHistoryDetailsSectionProps> = ({
  title,
  details,
  className,
}) => {
  if (!details.length) {
    return null;
  }

  return (
    <section className={cn(styles.detailsSection, className)}>
      <h4 className={styles.detailsSectionTitle}>{title}</h4>

      <dl className={styles.detailsList}>
        {details.map(({ key, label, value, isTechnicalValue, href }) => (
          <div key={key} className={styles.detailItem}>
            <dt className={styles.detailLabel}>{label}</dt>
            <dd
              className={cn(
                styles.detailValue,
                isTechnicalValue && styles.detailValueMonospace,
              )}
            >
              {href ? (
                <a
                  className={styles.detailLink}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {value}
                </a>
              ) : (
                value
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const BillingHistoryExpandIcon: FC<BillingHistoryExpandIconProps> = ({
  expanded,
  onExpand,
  record,
  expandLabel,
  collapseLabel,
  className,
}) => (
  <button
    type="button"
    className={cn(styles.expandButton, className)}
    onClick={(event) => onExpand(record, event)}
    aria-label={expanded ? collapseLabel : expandLabel}
  >
    {expanded ? (
      <CaretDownFilled className={styles.expandIcon} rev={undefined} />
    ) : (
      <CaretRightFilled className={styles.expandIcon} rev={undefined} />
    )}
  </button>
);

const createBillingHistoryColumns = ({
  formatMessage,
  blockReason,
  openModal,
  handleDownloadInvoice,
  handleDeleteOrder,
}: {
  formatMessage: IntlShape['formatMessage'];
  blockReason?: BillingProductBlockReason;
  openModal: (orderID: string) => void;
  handleDownloadInvoice: (invoiceId: string) => Promise<void>;
  handleDeleteOrder: (record: BillingHistoryOrderData) => void;
}): ColumnsType<BillingHistoryOrderData> => [
  {
    key: 'date',
    dataIndex: 'date',
    render: (text, record) => (
      <BillingHistoryCell
        withTooltip
        tooltipText={record.id}
        tooltipTrigger="click"
        className={styles.firstCell}
        text={text}
        label={formatMessage({
          id: 'billingHistory.label.date',
          defaultMessage: 'Order issuing date',
        })}
      />
    ),
  },
  {
    key: 'subject',
    dataIndex: 'subject',
    render: (text) => (
      <BillingHistoryCell
        withTooltip
        text={text}
        label={formatMessage({
          id: 'billingHistory.label.subject',
          defaultMessage: 'Subject',
        })}
      />
    ),
  },
  {
    key: 'statusText',
    dataIndex: 'statusText',
    render: (text) => (
      <BillingHistoryCell
        text={text}
        label={formatMessage({
          id: 'billingHistory.label.status',
          defaultMessage: 'Status',
        })}
      />
    ),
  },
  {
    key: 'action',
    dataIndex: 'action',
    render: (_, record) => {
      const blockedTooltip = blockReason
        ? formatMessage(i18n.billingHistoryBlockReasonMessages[blockReason])
        : undefined;

      return (
        <BillingHistoryCell
          withTooltip={!!record.transactionNumber}
          text={record.transactionNumber}
          label={formatMessage({
            id: 'billingHistory.label.action',
            defaultMessage: 'Actions',
          })}
        >
          <div className={styles.actions}>
            {record.canMarkAsPaid &&
              (blockedTooltip ? (
                <DisabledActionTooltip title={blockedTooltip}>
                  <Button type="link" disabled>
                    {formatMessage({
                      id: 'billingHistory.markAsPaidButton',
                      defaultMessage: 'Mark as paid',
                    })}
                  </Button>
                </DisabledActionTooltip>
              ) : (
                <Button type="link" onClick={() => openModal(record.id)}>
                  {formatMessage({
                    id: 'billingHistory.markAsPaidButton',
                    defaultMessage: 'Mark as paid',
                  })}
                </Button>
              ))}

            {record.markedAsPaid
              ? !record.transactionNumber && (
                  <Button
                    type="link"
                    onClick={() => handleDownloadInvoice(record.id)}
                  >
                    {formatMessage({
                      id: 'global.download',
                      defaultMessage: 'Download',
                    })}
                  </Button>
                )
              : record.invoiceURL &&
                record.status !== 'deleted' && (
                  <Button type="link" href={record.invoiceURL}>
                    {formatMessage({
                      id: 'global.download',
                      defaultMessage: 'Download',
                    })}
                  </Button>
                )}

            {record.canDelete &&
              (blockedTooltip ? (
                <DisabledActionTooltip title={blockedTooltip}>
                  <Button danger type="link" disabled>
                    {formatMessage({
                      id: 'billingHistory.deleteButton',
                      defaultMessage: 'Delete',
                    })}
                  </Button>
                </DisabledActionTooltip>
              ) : (
                <Button
                  danger
                  type="link"
                  onClick={() => handleDeleteOrder(record)}
                >
                  {formatMessage({
                    id: 'billingHistory.deleteButton',
                    defaultMessage: 'Delete',
                  })}
                </Button>
              ))}
          </div>
        </BillingHistoryCell>
      );
    },
  },
];

const renderBillingHistoryExpandedRow = (
  { formatMessage }: { formatMessage: IntlShape['formatMessage'] },
  record: BillingHistoryOrderData,
) => {
  const paymentDetails = [
    {
      key: 'amount',
      label: formatMessage({
        id: 'billingHistory.details.amount',
        defaultMessage: 'Amount',
      }),
      value: record.amount,
    },
    {
      key: 'dueDate',
      label: formatMessage({
        id: 'billingHistory.details.dueDate',
        defaultMessage: 'Due date',
      }),
      value: record.dueDate,
    },
    {
      key: 'invoiceId',
      label: formatMessage({
        id: 'billingHistory.details.invoiceId',
        defaultMessage: 'Invoice ID',
      }),
      value: record.invoiceID,
      isTechnicalValue: true,
    },
    {
      key: 'transactionNumber',
      label: formatMessage({
        id: 'billingHistory.details.transactionNumber',
        defaultMessage: 'Transaction number',
      }),
      value: record.transactionNumber,
      isTechnicalValue: true,
    },
    {
      key: 'orderId',
      label: formatMessage({
        id: 'billingHistory.details.orderId',
        defaultMessage: 'Order ID',
      }),
      value: record.id,
      isTechnicalValue: true,
    },
  ].filter(({ value }) => Boolean(value));

  const removalDetails = [
    {
      key: 'coinsSubtracted',
      label: formatMessage({
        id: 'billingHistory.details.coinsSubtracted',
        defaultMessage: 'Coins subtracted',
      }),
      value: record.coinsSubtracted,
    },
    {
      key: 'removalReason',
      label: formatMessage({
        id: 'billingHistory.details.removalReason',
        defaultMessage: 'Reason',
      }),
      value: record.removalReason,
    },
    {
      key: 'removalComment',
      label: formatMessage({
        id: 'billingHistory.details.removalComment',
        defaultMessage: 'Comment',
      }),
      value: record.removalComment,
    },
  ].filter(({ value }) => Boolean(value));

  const auditDetails = [
    {
      key: 'createdAt',
      label: formatMessage({
        id: 'billingHistory.details.createdAt',
        defaultMessage: 'Created at',
      }),
      value: record.createdAt,
    },
    {
      key: 'createdBy',
      label: formatMessage({
        id: 'billingHistory.details.createdBy',
        defaultMessage: 'Created by',
      }),
      value: record.createdBy,
      isTechnicalValue: true,
      href: record.createdBy
        ? `${adminBaseUri}/user/${record.createdBy}`
        : undefined,
    },
    {
      key: 'processedAt',
      label: formatMessage({
        id: 'billingHistory.details.processedAt',
        defaultMessage: 'Processed at',
      }),
      value: record.processedAt,
    },
    {
      key: 'processedBy',
      label: formatMessage({
        id: 'billingHistory.details.processedBy',
        defaultMessage: 'Processed by',
      }),
      value: record.processedBy,
      isTechnicalValue: true,
      href: record.processedBy
        ? `${adminBaseUri}/user/${record.processedBy}`
        : undefined,
    },
    {
      key: 'deletedAt',
      label: formatMessage({
        id: 'billingHistory.details.deletedAt',
        defaultMessage: 'Deleted at',
      }),
      value: record.deletedAt,
    },
    {
      key: 'deletedBy',
      label: formatMessage({
        id: 'billingHistory.details.deletedBy',
        defaultMessage: 'Deleted by',
      }),
      value: record.deletedBy,
      isTechnicalValue: true,
      href: record.deletedBy
        ? `${adminBaseUri}/user/${record.deletedBy}`
        : undefined,
    },
  ].filter(({ value }) => Boolean(value));

  return (
    <div className={styles.expandedContent}>
      {record.description && (
        <section className={styles.descriptionSection}>
          <h4 className={styles.detailsSectionTitle}>
            {formatMessage({
              id: 'billingHistory.details.description',
              defaultMessage: 'Description',
            })}
          </h4>

          <p className={styles.descriptionValue}>{record.description}</p>
        </section>
      )}

      <div className={styles.detailsGrid}>
        <BillingHistoryDetailsSection
          title={formatMessage({
            id: 'billingHistory.section.paymentDetails',
            defaultMessage: 'Payment details',
          })}
          details={paymentDetails}
        />

        <BillingHistoryDetailsSection
          title={formatMessage({
            id: 'billingHistory.section.removalDetails',
            defaultMessage: 'Removal details',
          })}
          details={removalDetails}
        />

        <BillingHistoryDetailsSection
          title={formatMessage({
            id: 'billingHistory.section.auditTrail',
            defaultMessage: 'Activity',
          })}
          details={auditDetails}
        />
      </div>
    </div>
  );
};

const renderBillingHistoryExpandIcon = (
  { expandLabel, collapseLabel }: BillingHistoryExpandIconLabels,
  {
    expanded,
    onExpand,
    record,
  }: Omit<BillingHistoryExpandIconProps, 'expandLabel' | 'collapseLabel'>,
) => (
  <BillingHistoryExpandIcon
    expanded={expanded}
    onExpand={onExpand}
    record={record}
    expandLabel={expandLabel}
    collapseLabel={collapseLabel}
  />
);

type BillingHistoryProps = {
  blockReason?: BillingProductBlockReason;
};

export const BillingHistory = ({ blockReason }: BillingHistoryProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const billingHistoryData = useBillingHistory();
  const ordersLoadingState = useAppSelector(selectOrdersLoadingState);
  const { orderID, visible, openModal, closeModal } = useMarkAsPaidModal();

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(
        `${BASE_URI}/api/billing/order/${invoiceId}/invoice/download?returnURL=true`,
        {
          credentials: 'include',
        },
      );

      if (response.ok) {
        const imageURL = await response.text();
        const blob = await fetch(imageURL, { credentials: 'same-origin' }).then(
          (imgResponse) => imgResponse.blob(),
        );

        const downloadLink = Object.assign(document.createElement('a'), {
          href: window.URL.createObjectURL(blob),
          download: `invoice_${invoiceId}`,
        });

        downloadLink.click();
      } else {
        notification.error({
          message: formatMessage({
            id: 'markAsPaidModal.error',
            defaultMessage: 'Something went wrong',
          }),
        });
      }
    } catch (error) {
      notification.error({
        message: formatMessage({
          id: 'global.network.error',
          defaultMessage: 'Network error',
        }),
      });
    }
  };

  const handleDeleteOrder = (record: BillingHistoryOrderData) => {
    Modal.confirm({
      title: formatMessage({
        id: 'billingHistory.deleteModal.title',
        defaultMessage: 'Delete unpaid order',
      }),
      content: formatMessage({
        id: 'billingHistory.deleteModal.content',
        defaultMessage:
          'This order has not been paid yet. Are you sure you want to delete it?',
      }),
      okText: formatMessage({
        id: 'billingHistory.deleteModal.okText',
        defaultMessage: 'Delete',
      }),
      cancelText: formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      }),
      okButtonProps: {
        danger: true,
      },
      centered: true,
      onOk: async () => {
        try {
          await dispatch(
            billingModel.thunks.deleteOrder({
              OrderID: record.id,
            }),
          ).unwrap();
        } catch (error) {
          notification.error({
            message:
              error instanceof Error
                ? error.message
                : formatMessage({
                    id: 'markAsPaidModal.error',
                    defaultMessage: 'Something went wrong',
                  }),
          });

          throw error;
        }
      },
    });
  };

  const columns = createBillingHistoryColumns({
    formatMessage,
    blockReason,
    openModal,
    handleDownloadInvoice,
    handleDeleteOrder,
  });
  const expandRowLabel = formatMessage({
    id: 'billingHistory.expandRow',
    defaultMessage: 'Expand row',
  });
  const collapseRowLabel = formatMessage({
    id: 'billingHistory.collapseRow',
    defaultMessage: 'Collapse row',
  });
  const expandedRowRender = renderBillingHistoryExpandedRow.bind(null, {
    formatMessage,
  });
  const expandIcon = renderBillingHistoryExpandIcon.bind(null, {
    expandLabel: expandRowLabel,
    collapseLabel: collapseRowLabel,
  });

  return (
    <>
      <Card
        title={
          <Title level={3} style={{ marginBottom: 0 }}>
            <FormattedMessage
              id="billingHistory.title"
              defaultMessage="Billing History"
            />
          </Title>
        }
        bordered={false}
        style={{ height: '100%' }}
        bodyStyle={{ paddingLeft: 0, paddingRight: 0, paddingTop: 1 }}
      >
        <Table
          size="small"
          showHeader={false}
          columns={columns}
          dataSource={billingHistoryData}
          expandable={{
            expandedRowRender,
            expandIcon,
          }}
          loading={ordersLoadingState !== 'succeeded'}
          scroll={{ x: true }}
          pagination={false}
        />
      </Card>

      <MarkAsPaidModal
        orderID={orderID}
        visible={visible}
        onCancel={closeModal}
      />
    </>
  );
};
